import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Table, DatePicker } from 'antd';
import dayjs from "dayjs";
import Card from "components/card";
import CrudOption from "components/dropdown/crudoption";
import { useCRUDAccess } from "utils/useCRUDAccess";
import { DATE_FORMAT_MDY, MODULE_TIMECARDS, TIME_FORMAT_12HM } from "variables/constants";
import TimeCardForm from "./TimeCardForm";
import PopupButton from "components/button/PopupButton";
import { NumberFormatter, handleTableChange } from "utils";
import { getTimeCardList, doTimeCardDelete } from "../../../../redux/timecard/timeCardState";
import SearchFilter from "components/search/SearchFilter";
import { ToolOutlined } from '@ant-design/icons';
import IndexModal from "components/modal";
import TimecardUpdateRequestForm from "../timecard-update-requests/components/TimecardUpdateRequestForm";


const TimeCardTable = ({...props}) => {

	const dispatch = useDispatch();
	const recordList = useSelector((state) => state.timeCard.timeCardList?.items);
	const pagination = useSelector((state) => state.timeCard.timeCardList?.pagination);
	const filters = useSelector((state) => state.timeCard.timeCardList?.filters);
	const requesting = useSelector((state) => state.timeCard.timeCardList?.requesting);
	const error = useSelector((state) => state.timeCard.timeCardList?.error);

	const doSearch = (filters) => dispatch(getTimeCardList(filters));
	const doDelete = (id) => dispatch(doTimeCardDelete(id));

	const user = useSelector((state) => state.auth.user);

	const { RangePicker } = DatePicker;

	const [currentSearchValue, setCurrentSearchValue] = useState('');
	const [dateRangeKey, setDateRangeKey] = useState(Date.now());

	const checkCRUDAccess = useCRUDAccess();
	const accessRights = checkCRUDAccess(MODULE_TIMECARDS, ['view', 'edit', 'delete'], true);
	
	const dateRangeValue = filters?.start_date && filters?.end_date 
		? [dayjs(filters.start_date), dayjs(filters.end_date)]
		: null;

	const [selectedTimeCard, setSelectedTimeCard] = useState(null);
	const [openRequestModal, setOpenRequestModal] = useState(false);
	
	const columns = [
		{
	        title: `Employee Name`,
	        dataIndex: 'tmc_employee_name',
	        key: 'tmc_employee_name',
	        width: '20%',
	        render:(tmc_employee_name) => <span>{tmc_employee_name}</span> 
	    },
		{
	        title: `Date`,
	        dataIndex: 'tmc_date',
	        key: 'tmc_date',
	        width: '10%',
	        render:(tmc_date) => <span>{dayjs(tmc_date).format(DATE_FORMAT_MDY)}</span> 
	    },
		{
	        title: `Day`,
	        dataIndex: 'tmc_date',
	        key: 'tmc_day',
	        width: '10%',
	        render:(tmc_date) => <span>{dayjs(tmc_date).format('dddd')}</span> 
	    },		
		{
			title: `Shift Schedule`,
			dataIndex: 'shift_string',
			key: 'shift_string',
			width: '18%',
			render: (shiftString) => (
				<span>{shiftString ?? 'Rest Day'}</span>
			),
		},
		{
	        title: `Punch In / Punch Out`,
	        dataIndex: 'tmc_punch_in',
	        key: 'tmc_punch_in',
	        width: '18%',
	        render:(tmc_punch_in, record) => <div>{tmc_punch_in && dayjs(tmc_punch_in).format(TIME_FORMAT_12HM)} - {record.tmc_punch_out && dayjs(record.tmc_punch_out).format(TIME_FORMAT_12HM)}</div>
	    },
		{
	        title: <div className="w-full text-right">Worked Hours</div>,
	        dataIndex: 'tmc_worked_hours',
	        key: 'tmc_worked_hours',
	        width: '13%',
	        render:(tmc_worked_hours) => <div className="pr-2 text-right">{tmc_worked_hours > 0 ? NumberFormatter(tmc_worked_hours, 2) : '-'}</div> 
	    },	
		    
	   user?.user_level !== 2 && {
			title: (
				<PopupButton
					id="add_time_card"
					title="Add Time Card"
					label="Add"
					btnClass="btn-small"
					moduleId={MODULE_TIMECARDS}
					action="add"
				>
					<TimeCardForm recordId="0" mode="edit" />
				</PopupButton>
			),
			dataIndex: 'tmc_id',
			key: 'tmc_id',
			width: '11%',
			render: (tmc_id, record) => (
				<CrudOption
					baseRoute="/console/time-cards"
					recordId={tmc_id}
					allowView={accessRights.view}
					allowEdit={accessRights.edit}
					allowDelete={accessRights.delete}
					additionalMenu={[
						{
							key: "update",
							icon: <ToolOutlined />,
							label: (
								<div
									onClick={() => {
										setSelectedTimeCard(record);
										setOpenRequestModal(true);
									}}
								>
									Timecard Update Request
								</div>
							),
							available: accessRights.view ? 1 : 0,
						},
					]}
				/>
			),
		},
	].filter(Boolean);

	useEffect(() => {
		dispatch(doSearch({paginate: pagination.per_page}));
	}, [dispatch, pagination.per_page]);

	const handleDateChange = (dates, dateStrings) => {
		const newFilters = {
			paginate: pagination.per_page,
			page: 1
		};

		if (dates && dates[0] && dates[1]) {
			newFilters.start_date = dateStrings[0];
			newFilters.end_date = dateStrings[1];
		} 

		if (currentSearchValue) {
			newFilters.keyword = currentSearchValue;
		}

		doSearch(newFilters);
	};

	const handleSearch = (searchFilters) => {
		const searchValue = searchFilters.keyword || '';
		setCurrentSearchValue(searchValue);
		
		setDateRangeKey(Date.now());
		
		const newFilters = {
			paginate: pagination.per_page,
			page: 1
		};
		
		if (searchValue) {
			if (filters?.start_date && filters?.end_date) {
				newFilters.start_date = filters.start_date;
				newFilters.end_date = filters.end_date;
			}
			newFilters.keyword = searchValue;
		} 
		
		doSearch(newFilters);
	};
	
  	return (
	    <Card extra={"w-full h-full p-4"}>
	      	<div className="h-full w-full overflow-x-auto">
	      	
				<div className="grid grid-cols-1 md:grid-cols-2 gap-2 items-center">
					{user?.user_level !== 5 && (
						<div className="p-1">
							<SearchFilter
								placeholder="Search by Employee Name"
								searchHandler={handleSearch}
								filters={filters}
								pagination={pagination}
							/>
						</div>
					)}

					<div className={`p-2 flex justify-end ${user?.user_level === 5 ? 'col-span-2' : 'col-span-1'}`}>
						<RangePicker
							key={dateRangeKey}
							className="h-9 w-full md:w-72 !rounded-lg input-field border"
							format="YYYY-MM-DD"
							onChange={handleDateChange}
							placeholder={['Start Date', 'End Date']}
							allowClear
							value={dateRangeValue}
						/>
					</div>
				</div>

	      		<Table
		        	dataSource={recordList} 
		        	columns={columns} 
		            rowKey={(record) => record.tmc_id}
					pagination={{...pagination, showTotal: (total, range) => `${range[0]} - ${range[1]} of ${total} records`,}}    
					scroll={{ x: 'max-content' }}
					onChange={handleTableChange(doSearch, filters)}               	
	        	/>
				<IndexModal
					open={openRequestModal}
					onClose={() => setOpenRequestModal(false)}
					title={"Timecard Update Request"}
					width = {650}
				>
					<TimecardUpdateRequestForm
						timecard={selectedTimeCard}
						onCancel={() => setOpenRequestModal(false)}
					/>
				</IndexModal>
	      	</div>
	    </Card>
  	);
};

export default (TimeCardTable);
